<script lang="ts">import { stateUi } from "@infinitro/config";
let { id = "custom-toggle", checked = $bindable(false), label = null, description = null, disabled = false, required = false, name = null, labelPosition = "right", labelUppercase = true, onClick } = $props();
const useAlternative = $derived($stateUi.toggle.toggleStyle === "alternative");
const useAlternative2 = $derived($stateUi.toggle.toggleStyle === "alternative2");
</script>

<div class="toggle-wrapper">
	<div class="custom-toggle-container {labelPosition === 'left' ? 'left-layout' : ''}">
		{#if label && labelPosition === 'left'}
			<div class="label-container">
				<label for={id} class="custom-toggle-text" class:label-uppercase={labelUppercase}>
					{label}
					{#if required}
						<span class="required-indicator">*</span>
					{/if}
				</label>
			</div>
		{/if}

		<span class="custom-toggle-input-container">
			<label class="switch">
				<input
					type="checkbox"
					{id}
					{name}
					bind:checked
					{disabled}
					{required}
					aria-required={required}
					onclick={(e) => {
						if (!disabled) onClick?.(e, !checked);
					}}
				/>
				<span class="switch-container {required ? 'required' : ''} {disabled ? 'disabled' : ''}">
					<span
						class="rectangle-background"
						class:rectangle-background-alternative2={useAlternative2}
					></span>
					<span
						class="rectangle-inactive"
						class:rectangle-inactive-alternative={useAlternative}
						class:rectangle-inactive-alternative2={useAlternative2}
					></span>
					<span class="rectangle-overlay" class:rectangle-overlay-alternative2={useAlternative2}
					></span>
				</span>
			</label>
		</span>

		{#if label && labelPosition === 'right'}
			<div class="label-container">
				<label for={id} class="custom-toggle-text" class:label-uppercase={labelUppercase}>
					{label}
					{#if required}
						<span class="required-indicator">*</span>
					{/if}
				</label>
			</div>
		{/if}
	</div>

	{#if description}
		<p class="description">{description}</p>
	{/if}
</div>

<style>
	.toggle-wrapper {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
}

	.custom-toggle-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 0.5rem;
		font-family: var(--font-main);
}

	.left-layout {
		justify-content: space-between;
}

	.label-container {
		display: flex;
		flex-direction: column;
}

	.custom-toggle-input-container {
		position: relative;
		display: flex;
		align-items: center;
}

	.switch {
		position: relative;
		display: inline-block;
		cursor: pointer;
		-webkit-user-select: none;
		   -moz-user-select: none;
		        user-select: none;
		vertical-align: middle;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 200ms;
		transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
		width: 32px;
		height: 20px;
}

	.switch input {
		position: absolute;
		height: 0px;
		width: 0px;
		opacity: 0;
}

	.switch-container {
		position: absolute;
		top: 0px;
		left: 0px;
		right: 0px;
		bottom: 0px;
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 300ms;
}

	.rectangle-background {
		position: absolute;
		left: 6.25%;
		right: 6.25%;
		top: 10%;
		bottom: 10%;
		border-radius: 96px;
		background: var(--color-background-100);
}

	.rectangle-inactive {
		position: absolute;
		left: 12.5%;
		right: 50%;
		top: 20%;
		bottom: 20%;
		border-radius: 56px;
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 300ms;
		background: var(--color-neutral-900);
}

	.rectangle-overlay {
		position: absolute;
		left: 25%;
		right: 62.5%;
		top: 40%;
		bottom: 40%;
		border-radius: 9999px;
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 300ms;
		background: var(--color-background-100);
}

	.rectangle-background-alternative2 {
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-tertiary-700) 100%
		);
	}

	.rectangle-background-alternative2::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		padding: 1px;
		background: linear-gradient(180deg, var(--color-text-500) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
	}

	.rectangle-inactive-alternative2 {
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-tertiary-700) 100%
		);
	}

	.rectangle-inactive-alternative2::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		padding: 1px;
		background: linear-gradient(180deg, var(--color-text-500) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
	}

	.rectangle-overlay-alternative2 {
		background-color: transparent;
}

	/* Checked state styles */
	input:checked + .switch-container .rectangle-background {
		background: var(--color-success-900);
		box-shadow: 0px 0px 7px 0px color-mix(in srgb, var(--color-success-100) 50%, transparent);
	}

	input:checked + .switch-container .rectangle-inactive {
		left: 50%;
		right: 12.5%;
		opacity: 1;
		background: var(--color-success-500);
		border-color: var(--color-success-100);
		box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.55);
}

	input:checked + .switch-container .rectangle-inactive-alternative {
		background: var(--color-success-500);
		border-color: var(--color-success-100);
	}

	input:checked + .switch-container .rectangle-overlay {
		left: 62.5%;
		right: 25%;
		background: var(--color-success-900);
		box-shadow: 0px 2px 4px rgba(27, 28, 29, 0.04);
}

	input:checked + .switch-container .rectangle-background.rectangle-background-alternative2 {
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-tertiary-700) 100%
		);
		border: 1px solid var(--color-primary-500);
		box-shadow: none;
	}

	/* matar el pseudo-elemento cuando está activo */
	input:checked + .switch-container .rectangle-background-alternative2::before {
		content: none;
	}

	input:checked + .switch-container .rectangle-inactive.rectangle-inactive-alternative2 {
		background: var(--color-primary-500);
		border: 1px solid var(--color-text-100);
		box-shadow: none;
	}

	input:checked + .switch-container .rectangle-inactive-alternative2::before {
		content: none;
	}

	input:checked + .switch-container .rectangle-overlay.rectangle-overlay-alternative2 {
		background-color: transparent;
		--tw-shadow: 0 0 #0000;
		--tw-shadow-colored: 0 0 #0000;
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}

	/* Required state styles */
	.required.switch-container {
		border-radius: 96px;
		border-width: 1px;
		--tw-border-opacity: 1;
		border-color: rgb(239 68 68 / var(--tw-border-opacity, 1));
}

	.required-indicator {
		margin-left: 0.25rem;
		--tw-text-opacity: 1;
		color: rgb(239 68 68 / var(--tw-text-opacity, 1));
}

	/* Disabled state styles */
	.disabled {
		cursor: not-allowed;
		opacity: 0.3;
}

	.custom-toggle-text {
		font-size: 10px;
		font-weight: 600;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	@media (min-width: 640px) {
		.custom-toggle-text {
				font-size: 0.875rem;
				line-height: 1.25rem;
		}
	}

	.description {
		font-size: 0.75rem;
		line-height: 1rem;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}

	.label-uppercase {
		text-transform: uppercase;
		letter-spacing: 0.025em;
}
</style>
